import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useTaskTypes } from "components/Actions/dialogs/Devices/CreateTask/hooks/useTaskTypes";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { FILTER_LIST_FIELD_NAMES } from "shared/constants/filters";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import {
    filteredExistingItems,
    handleFilterOptionsStructure,
    isSpecIdFilter,
    transformIdArrayWithSpecIdNotAssigned
} from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useDecoder } from "shared/hooks/useDecoder";
import { useParamsFromUrlAndBrowser } from "shared/hooks/useParamsFromUrlAndBrowser";
import { ENDPOINT_PATH_SEGMENTS, FILTER_NAMES } from "shared/types/enums";
import { Filter, FilteredValue } from "shared/types/interfaces";
import { FilterDropdownOptions, FilterId } from "shared/types/types";

import {
    checkFilteredValues,
    filterExistingFilterNames,
    findActualFilters,
    getBooleanFilteredValues,
    getDecodedFilterValue,
    getFilterNames,
    handleLoading,
    transformedFilteredItems,
    updateFilterValues,
    updateQueryValue
} from "../functions";

export const useFilteredValues = () => {
    const {
        queryParams,
        filterQueryParams,
        filteredValues: mainFilteredValues,
        filterDropdownOptions,
        setFilteredValues,
        setQueryParams,
        resetFilterParams
    } = useContext(ParamsContext);

    const {
        isInnerTable,
        tableName,
        innerTableQueryParams,
        innerTableFilteredValues,
        setInnerTableParams,
        resetInnerTableFilterParams
    } = useContext(TableContext);

    const { t } = useTranslation();

    const { currentParams } = useParamsFromUrlAndBrowser();
    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const { tasks } = useTaskTypes();
    const { decodeArray } = useDecoder();

    const filterNames = getFilterNames(currentParams);
    const filtersExist = filterNames.length > 0;

    const [isLoading, setIsLoading] = useState(
        handleLoading(isInnerTable, filtersExist)
    );

    const filteredValues = checkFilteredValues(
        isInnerTable,
        innerTableFilteredValues,
        mainFilteredValues
    );

    const existingFilterNames = filterExistingFilterNames(
        filterNames,
        FILTER_LIST_FIELD_NAMES,
        tableName
    );

    const companyIdOptions = filterDropdownOptions[FILTER_NAMES.CompanyId];

    useEffect(() => {
        let isSubscribed = true;

        const getFilteredValues = async () => {
            try {
                setIsLoading(true);

                const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];
                const rootCompanyQuery = `${FILTER_NAMES.RootCompany}=${rootCompanyId}`;

                const endpoints = existingFilterNames.map(filterName =>
                    getData(
                        `${tableName}${ENDPOINT_PATH_SEGMENTS.FilterList}?${rootCompanyQuery}&field=${filterName}`
                    )
                );

                const results = await Promise.all(endpoints);

                if (isSubscribed) {
                    const getValueAndOptions = () => {
                        const filteredValue: FilteredValue[] = [];
                        const filterOptions: FilterDropdownOptions = {};

                        results.forEach(result => {
                            const {
                                config,
                                data: options
                            }: { config: any; data: Filter[] } = result;

                            const filterName: FILTER_NAMES =
                                config.url.split("field=")[1];

                            const sortedOptions = handleFilterOptionsStructure(
                                options,
                                filterName,
                                tableName,
                                tasks,
                                t
                            );

                            const filterValue = currentParams[filterName];

                            const decodedFilterValue = getDecodedFilterValue(
                                filterName,
                                filterValue,
                                decodeArray
                            );

                            const transformedValues = transformedFilteredItems(
                                filteredExistingItems(
                                    sortedOptions,
                                    decodedFilterValue
                                )
                            );

                            filteredValue.push({
                                filterName,
                                value: transformedValues
                            });

                            filterOptions[filterName] = sortedOptions;
                        });

                        return { filteredValue, filterOptions };
                    };

                    const {
                        filteredValue: value,
                        filterOptions: allFilterOptions
                    } = getValueAndOptions();

                    const booleanFilteredValues = getBooleanFilteredValues(
                        filterNames,
                        tableName,
                        currentParams
                    );

                    setFilteredValues(
                        [...value, ...booleanFilteredValues],
                        allFilterOptions
                    );
                }
            } catch (error) {
                if (isSubscribed && !isCanceled(error)) {
                    handleResponse(error);
                }
            }

            setIsLoading(false);
        };

        if (!isInnerTable && filtersExist && companyIdOptions) {
            getFilteredValues();
        }

        return () => {
            if (!isInnerTable) {
                isSubscribed = false;
                cancelSource.cancel(API_IS_BEING_CANCELLED);

                setFilteredValues([]);
                setIsLoading(false);
            }
        };

        // eslint-disable-next-line
    }, [companyIdOptions]);

    const handleDelete = (filterName: FILTER_NAMES, id: FilterId) => {
        const isSpecId = isSpecIdFilter(filterName);

        const allParams = isInnerTable
            ? innerTableQueryParams
            : filterQueryParams;

        const encodedID = isSpecId ? id : encodeURIComponent(id);

        const currentQueryValues = allParams[filterName] as string | string[];

        const currentQueryValuesArray = Array.isArray(currentQueryValues)
            ? currentQueryValues
            : [currentQueryValues];

        const updatedCurrentQueryValues = transformIdArrayWithSpecIdNotAssigned(
            filterName,
            currentQueryValuesArray
        );

        const updatedQueryValue = updateQueryValue(
            updatedCurrentQueryValues,
            encodedID
        );

        const actualFilter = findActualFilters(filteredValues, filterName);

        const updatedFilteredValues = updateFilterValues(actualFilter, id);

        const paramsPayload = {
            [filterName]: updatedQueryValue
        };

        const filteredValuesPayload = {
            filterName,
            value: updatedFilteredValues
        };

        (isInnerTable ? setInnerTableParams : setQueryParams)({
            params: paramsPayload,
            resetPage: true,
            filteredValues: filteredValuesPayload
        });
    };

    const handleClearAll = () => {
        isInnerTable ? resetInnerTableFilterParams() : resetFilterParams();
    };

    return { isLoading, filteredValues, handleDelete, handleClearAll };
};
